import useWebsocket from "@/hooks/useWebsocket";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { DistributedRewardResponse, NotificationType, RewardsApi, WebsocketEvent } from "@devour/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, useState } from "react";
import { useSelector } from "react-redux";
import { GuildPrizeWonDialogFromDistributedReward } from "@/pages/guilds/GuildPrizeWonDialog";
import PromoTokenWonModal from "@/components/modals/promoToken/PromoTokenWonModal";
import MerchFulfillmentWonModal from "@/components/modals/MerchFulfillmentWonModal";
import {useLocation, useSearchParams} from "react-router";
import {routeRendersOwnRewardNotifications} from "@/customHubs/registry";
import {useGetDistributedReward} from "@/hooks/useGetDistributedReward";
import LeaderboardWinToast from "@/components/LeaderboardWinToast";

export const useGetDistributedRewardsOwn = () => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const client = new RewardsApi(getConfig(fullToken));
    return useQuery({
        queryFn: async () => {
            return client.getDistributedRewardsOwn({
                webNotificationDismissed: false,
            });
        },
        queryKey: ["getDistributedRewardsOwn"],
        enabled: Boolean(fullToken),
        /*
         * The websocket REWARD_DISTRIBUTED event is the primary trigger
         * for surfacing new reward modals (PROMO_TOKEN /
         * SPONSORED_COMPETITION_WINNER / MERCH_FULFILLMENT). On iOS
         * WebView and some flaky network conditions, the websocket
         * connection can drop without recovering, leaving freshly-won
         * rewards stuck in cache until the next mount. These hints
         * are the safety net — focus + 30s poll catches the gap
         * without requiring an app restart.
         */
        refetchOnWindowFocus: true,
        refetchInterval: 30000,
    });
};

export const useDismissDistributedReward = () => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const client = new RewardsApi(getConfig(fullToken));
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            return client.updateDistributedRewardsIdDismissNotificationWeb({
                id: id,
            });
        },
        mutationKey: ["dismissDistributedReward"],
        onSuccess: () => {
            // should we invalidate the query here?
            queryClient.invalidateQueries({ queryKey: ["getDistributedRewardsOwn"] });
        },
    });
};


export const GlobalRewardNotifications = ({children}: PropsWithChildren) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const distributedRewardId = searchParams.get("distribute_reward");
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    /*
     * Some campaigns mount their own branded modal for MERCH_FULFILLMENT —
     * RTTR's MinelabRttrShipModal, Full Throttle's PolarisPrizeNotifier — so
     * the generic one must not double-render on their routes.
     *
     * Read from the custom-hub registry, which already owns the slug list and
     * the exact-match boundary. This was two hardcoded regexes, one per
     * campaign, growing by one every launch (D.7). A campaign now declares it
     * as config; nothing here changes.
     *
     * If the user navigates AWAY mid-claim this component is still mounted on
     * the destination page via PageWrapper, so the generic modal is the
     * fallback and the claim flow stays reachable.
     */
    const routeOwnsRewardModal = routeRendersOwnRewardNotifications(location.pathname);
    const {data: queryParamsDistributedReward} = useGetDistributedReward(distributedRewardId, fullToken);
    const {data: distributedRewards} = useGetDistributedRewardsOwn();
    const queryClient = useQueryClient();
    useWebsocket(WebsocketEvent.REWARD_DISTRIBUTED, () => {
        queryClient.invalidateQueries({ queryKey: ["getDistributedRewardsOwn"] });
    });
    const {
        mutateAsync: dismissReward,
    } = useDismissDistributedReward();

    /*
     * Session-only dismiss set — rewards listed here are hidden for
     * THIS browser session but stay undismissed server-side. Reload
     * the page and they re-surface. Used by the MERCH_FULFILLMENT
     * modal so a user who accidentally closes the shipping form (or
     * navigates away mid-fill) can still come back to it later. For
     * non-shipping reward types (PROMO_TOKEN, SPONSORED_COMPETITION_
     * WINNER) close = server-side dismiss as before — those are
     * one-time popups with no follow-up action required.
     */
    const [sessionHiddenIds, setSessionHiddenIds] = useState<Set<string>>(new Set());

    function onDismiss(rewardId: string) {
        void dismissReward(rewardId);
    }

    function onLocalHide(rewardId: string) {
        setSessionHiddenIds(prev => {
            const next = new Set(prev);
            next.add(rewardId);
            return next;
        });
    }

    const renderRewardNotification = (reward: DistributedRewardResponse) => {
        switch (reward.notificationMetadata?.notificationType) {
            case NotificationType.SPONSORED_COMPETITION_WINNER:
                return (
                    <GuildPrizeWonDialogFromDistributedReward
                        isOpen={true}
                        onClose={() => onDismiss(reward.id)}
                        onDismiss={() => onDismiss(reward.id)}
                        reward={reward}
                    />
                );
            case NotificationType.PROMO_TOKEN:
                return (
                    <PromoTokenWonModal
                        isOpen={true}
                        onClose={() => onDismiss(reward.id)}
                        reward={reward}
                    />
                );
            case NotificationType.PRIZE_POOL:
                /*
                 * A leaderboard win is a toast, not a modal — nothing to claim and no
                 * decision in it. Fired as a side effect, so nothing renders here.
                 */
                return (
                    <LeaderboardWinToast
                        key={reward.id}
                        reward={reward}
                        onShown={() => onDismiss(reward.id)}
                    />
                );
            case NotificationType.MERCH_FULFILLMENT:
                if (routeOwnsRewardModal) return null;
                return (
                    <MerchFulfillmentWonModal
                        isOpen={true}
                        onClose={() => onLocalHide(reward.id)}
                        onClaimed={() => onDismiss(reward.id)}
                        reward={reward}
                    />
                );
            default:
                return null;
        }
    };

    /*
     * Dedupe: queryParamsDistributedReward (from ?distribute_reward=id)
     * may also be present in the distributedRewards.rewards list when it
     * is still undismissed. Without this guard, the same reward modal
     * mounts twice — two the legacy modal portals stacking on top of each
     * other.
     */
    const ownRewardIds = new Set((distributedRewards?.rewards ?? []).map(r => r.id));
    const shouldRenderQueryParamReward = Boolean(queryParamsDistributedReward) &&
        !ownRewardIds.has(queryParamsDistributedReward!.id) &&
        !sessionHiddenIds.has(queryParamsDistributedReward!.id);

    return (
        <>
            {distributedRewards?.rewards
                .filter(r => !sessionHiddenIds.has(r.id))
                .map((reward) =>
                    <span key={reward.id}>{renderRewardNotification(reward)}</span>)}
            {shouldRenderQueryParamReward && renderRewardNotification(queryParamsDistributedReward!)}
            {children}
        </>
    );
};